import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/api";
import { useApi, Panel, PageHead, Stat, DataTable, StatusBadge, inr } from "@/crm/ui";

export default function Approvals() {
  const nav = useNavigate();
  const queue = useApi("/crm/approvals");
  const d = queue.data || {};
  const s = d.summary || {};

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <PageHead eyebrow="CRM" title="Approvals queue"
                subtitle="Everything waiting on a decision — bank applications in process, sanctioned cases awaiting disbursement and payouts pending approval." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Applications in process" value={s.applications ?? "—"} testid="ap-applications" />
        <Stat label="Awaiting disbursement" value={s.awaiting_disbursement ?? "—"} testid="ap-awaiting" />
        <Stat label="Payouts pending" value={s.payouts ?? "—"} testid="ap-payouts" />
        <Stat label="Payout value" value={inr(s.payout_amount)} testid="ap-payout-amount" />
      </div>

      <Panel title={`Applications awaiting bank decision (${(d.applications || []).length})`}
             testid="pending-applications-panel">
        <DataTable testid="pending-applications-table" rows={d.applications}
                   empty="No applications are pending a decision."
                   onRowClick={(r) => nav(`/crm/leads/${r.lead_id}`)}
                   columns={[{ key: "application_id", label: "Application" },
                     { key: "customer_name", label: "Customer" }, { key: "bank_name", label: "Bank" },
                     { key: "location_name", label: "Location" }, { key: "product_name", label: "Product" },
                     { key: "login_amount", label: "Login", right: true, render: (r) => inr(r.login_amount) },
                     { key: "login_date", label: "Login Date", render: (r) => fmtDate(r.login_date) },
                     { key: "pending_days", label: "Pending Days", right: true },
                     { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                     { key: "hold_reason", label: "Hold Reason" },
                     { key: "open", label: "", render: (r) => (
                       <Button size="sm" variant="outline" className="bg-white text-[10px]"
                               data-testid={`approval-open-${r.application_id}`}
                               onClick={(e) => { e.stopPropagation(); nav(`/crm/leads/${r.lead_id}`); }}>
                         Open lead</Button>) }]} />
      </Panel>

      <Panel title={`Sanctioned — awaiting disbursement (${(d.awaiting_disbursement || []).length})`}
             testid="awaiting-disbursement-panel"
             aside={<Button size="sm" variant="outline" className="bg-white"
                            onClick={() => nav("/crm/disbursement")} data-testid="go-disbursement-btn">
               Open disbursement desk</Button>}>
        <DataTable testid="awaiting-disbursement-table" rows={d.awaiting_disbursement}
                   empty="No sanctioned cases pending disbursement."
                   columns={[{ key: "application_id", label: "Application" },
                     { key: "customer_name", label: "Customer" }, { key: "bank_name", label: "Bank" },
                     { key: "approved_amount", label: "Sanctioned", right: true, render: (r) => inr(r.approved_amount) },
                     { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                     { key: "approval_date", label: "Approval Date", render: (r) => fmtDate(r.approval_date) },
                     { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
      </Panel>

      <Panel title={`Payouts pending approval (${(d.payouts || []).length})`} testid="pending-payouts-panel"
             aside={<Button size="sm" variant="outline" className="bg-white"
                            onClick={() => nav("/crm/payouts")} data-testid="go-payouts-btn">
               Open payouts</Button>}>
        <DataTable testid="pending-payouts-table" rows={d.payouts} empty="No payouts pending."
                   columns={[{ key: "payout_id", label: "Payout" },
                     { key: "application_id", label: "Application" }, { key: "bank_name", label: "Bank" },
                     { key: "employee_id", label: "Employee" }, { key: "partner_id", label: "Partner" },
                     { key: "net_payable", label: "Net Payable", right: true, render: (r) => inr(r.net_payable) },
                     { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
      </Panel>
    </div>
  );
}
